

import {Component, Prop, Watch} from "vue-property-decorator";
import {UserLevel} from "shared/entities/user";
import * as icons from "@fortawesome/free-solid-svg-icons";
import AuthMixin from "@/mixins/auth.mixin";
import {DocumentEntryFileType, DocumentEntryType, IDocumentEntry} from "shared/entities/docuement-entry";
import {Api} from "shared/api";
import {apiHandler} from "@/services/api-handler";
import FileUpload from "@/components/file-upload.vue";
import DownloadButton from "@/components/download-button.vue";
import {CreateDocumentRequest} from "shared/api/routes/document";

@Component({
    components: {FileUpload, DownloadButton}
})
export default class DocumentsSection extends AuthMixin {
    @Prop({default: false})
    v2!: boolean;

    UserLevel = UserLevel;
    icons = icons;
    DocumentEntryType = DocumentEntryType;
    DocumentEntryFileType = DocumentEntryFileType;
    currentDocuments: IDocumentEntry[] = [];
    parents: IDocumentEntry[] = [];
    showDocumentForm = false;
    showDirectoryForm = false;

    newDocument: CreateDocumentRequest = {
        title: "",
        description: "",
        icon: "file",
        type: DocumentEntryType.File,
        fileType: DocumentEntryFileType.Other,
        file: "",
        parent: null,
    };

    newDirectory = {
        title: "",
        description: "",
    };

    get currentPath(): string {
        return "/ " + this.parents.map(p => p.title).join(" / ");
    }

    get currentParent(): IDocumentEntry | null {
        if (this.parents.length === 0) {
            return null;
        }
        return this.parents[this.parents.length - 1];
    }

    get orderedDocuments(): IDocumentEntry[] {
        const newOrder = Array.from(this.currentDocuments);
        newOrder.sort((a, b) => {
            if (a.type === DocumentEntryType.Directory && b.type === DocumentEntryType.File) {
                return -1;
            }
            if (a.type === DocumentEntryType.File && b.type === DocumentEntryType.Directory) {
                return 1;
            }
            return a.title.localeCompare(b.title);
        });
        return newOrder;
    }

    async mounted() {
        await this.getDocuments();
    }

    toggleDirectoryForm() {
        this.showDirectoryForm = !this.showDirectoryForm;
        // Only one form open at a time, matching the old behaviour where each
        // heading was hidden while the other form was showing.
        if (this.showDirectoryForm) {
            this.showDocumentForm = false;
        }
    }

    toggleDocumentForm() {
        this.showDocumentForm = !this.showDocumentForm;
        if (this.showDocumentForm) {
            this.showDirectoryForm = false;
        }
    }

    async back() {
        this.parents.pop();
        await this.getDocuments();
    }

    async changeTo(document: IDocumentEntry) {
        this.parents.push(document);
        await this.getDocuments();
    }

    async onNewDocument() {
        if (!this.newDocument.title) {
            alert("Please enter a title for the new document");
            return;
        }
        if (!this.newDocument.file) {
            alert("Please select a file for the new document");
            return;
        }
        this.newDocument.parent = this.currentParent ? this.currentParent.id : null;
        await apiHandler(() => Api.document.Post(this.newDocument));
        this.newDocument.title = "";
        this.newDocument.description = "";
        this.newDocument.file = "";
        this.showDocumentForm = false;
        await this.getDocuments();
    }

    async onNewDirectory() {
        if (!this.newDirectory.title) {
            alert("Please enter a title for the new directory");
            return;
        }
        await apiHandler(() => Api.document.Post({
            title: this.newDirectory.title,
            description: this.newDirectory.description,
            icon: "faFolder",
            type: DocumentEntryType.Directory,
            fileType: DocumentEntryFileType.Other,
            file: "",
            parent: this.currentParent ? this.currentParent.id : null,
        }));
        this.newDirectory.title = "";
        this.newDirectory.description = "";
        this.showDirectoryForm = false;
        await this.getDocuments();
    }

    async onDelete(document: IDocumentEntry) {
        if (!confirm(`Are you sure you want to delete ${document.title}?`)) {
            return;
        }
        await apiHandler(() => Api.document[document.id].Delete());
        await this.getDocuments();
    }

    @Watch("newDocument.file")
    private onNewDocumentFileChanged() {
        const extension = this.newDocument.file.substr(this.newDocument.file.lastIndexOf(".") + 1).toLowerCase();
        switch (extension) {
        case "pdf":
            this.newDocument.fileType = DocumentEntryFileType.PDF;
            this.newDocument.icon = "faFilePdf";
            break;
        case "mp4":
            this.newDocument.fileType = DocumentEntryFileType.Video;
            this.newDocument.icon = "faFileVideo";
            break;
        case "jpg":
            this.newDocument.fileType = DocumentEntryFileType.Image;
            this.newDocument.icon = "faFileImage";
            break;
        case "png":
            this.newDocument.fileType = DocumentEntryFileType.Image;
            this.newDocument.icon = "faFileImage";
            break;
        case "gif":
            this.newDocument.fileType = DocumentEntryFileType.Image;
            this.newDocument.icon = "faFileImage";
            break;
        default:
            this.newDocument.fileType = DocumentEntryFileType.Other;
            this.newDocument.icon = "faFile";
            break;
        }
    }

    private async getDocuments() {
        if (this.currentParent !== null) {
            const document = this.currentParent;
            this.currentDocuments = await apiHandler(() => Api.document[document.id].Get());
        } else {
            this.currentDocuments = await apiHandler(() => Api.document.Get());
        }
    }
}

